import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    groupingSchemas,
    SalesDocument,
    SalesDocumentPosition,
    State,
    DocumentPositionGroup,
    DocumentAttachment,
    SalesDocumentType,
    StaticPositionDetailsService,
    SalesDocumentTypeDict,
    DocumentPosition,
    Delivery,
    DeliveryStatus,
    DeliveryStatuses,
    DeliveryStatusesColors,
    Synced,
    PersonsService,
} from '@icc/b2b/shared';
import { ConfigService, core } from '@icc/shared';
import { ExchangeRateService, Currency } from '@icc/shared/currency';
import { _, DetailsTreeNode, logger, parseDate } from '@icc/shared/helpers';
import { ComplexConfiguratorField } from '@icc/shared/types/complex/ComplexConfiguratorField';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest, of, from, zip, Subject, BehaviorSubject, ReplaySubject, defer } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { changePosition, openDoc } from '../+state/sales-documents.actions';
import {
    getBuyDocumentsForSelectedDocument,
    getBuyPositionsForSelectedDocument,
    getSelectedSalesDocumentPositions,
    getSalesDocumentPositions,
    getSelectedSales,
    getSelectedId,
    getDocumentsAttachments,
    getDocDeliveries,
    getDocumentDeliveries,
    getSynced,
} from '../+state/sales-documents.selectors';
import { SalesDocumentViewMode } from '../models/view-modes.model';
import { PositionService } from '../position.service';
import { SalesDocumentService } from '../sales-document.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfiguratorLoaderService } from '@icc/b2b/configurator';
import { PriceVersionService } from '../price-version-selector/price-version.service';
import { OfferValidityService } from '../offer-validity/offer-validity.service';
import { SyncService } from '../sync.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { DocumentManagerService } from '../document-manager.service';
import { SalesDocumentPersonsService } from '../sales-document-persons.service';
import { DialogService } from '@icc/ui';
import { DeliverySignaturePreviewComponent } from '../delivery-signature-preview/delivery-signature-preview.component';
import { StatsService } from '@icc/shared/helpers/b2b';
import { IccAppearance } from 'libs/ui/src/lib/enums/appearance';
import { DocumentAction } from '../sales-document-summary/document-action';
import { HttpClient } from '@angular/common/http';
import { translateConfiguration, prepareTranslatedData } from '@icc/shared/helpers';
import { ConfigurationType } from "@icc/shared/types";
import { ProductsService } from 'libs/b2b/trade/src/lib/products.service';

@Component({
    selector: 'icc-sales-document',
    templateUrl: './sales-document.component.html',
    styleUrls: ['./sales-document.component.scss'],
})
export class SalesDocumentComponent implements OnInit, OnDestroy, AfterViewInit {

    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    documentUuid = '';
    documentNumber = '';
    documentType: SalesDocumentType = SalesDocumentType.Quotation;
    groupingControl = new UntypedFormControl('0');
    summingControl = new UntypedFormControl('0');
    sortingControl = new UntypedFormControl('created');
    salesDocumentSellerCompanyLicense = false;
    canPositionsBeEdited = false;
    canSalesProducerNotesBeEdited = false;
    canBuyProducerNotesBeEdited = false;
    canSalesProducerNotesRead = false;
    canBuyProducerNotesRead = false;
    canRequestComplaintV1 = false;
    departments: Record<number, { name: string; priority: number }> = {};
    buyProducerNotesEdit: Record<string, string> = {};
    buyDocumentNotesEditable: Record<string, boolean> = {};
    salesProducerNotesEdit = '';

    mode: SalesDocumentViewMode = 'sale';
    mode$ = new BehaviorSubject<SalesDocumentViewMode>('sale');

    hasInactiveProducts = false;

    allPositions$ = this.store.select(getSelectedSalesDocumentPositions);
    positions$ = this.allPositions$.pipe(
        map((positions) => positions.filter(pos => !pos.isDeleted)),
        map((pos) =>
            pos.map((p) => ({
                ...p,
                configuration: p.configuration ? core.parseJson(p.configuration) : null,
                priceDiscounts: p.priceDiscounts ? core.parseJson(p.priceDiscounts) : [],
            }))
        )
    );
    positionsDetails = new Map<string, DetailsTreeNode>();
    positionsShowDetails = new Map<string, boolean>();
    positionsTitles = new Map<string, string[]>();
    positionsIndexes = new Map<string, string>();

    openedDocumentId$ = this.store.select(getSelectedId);
    openedDocument$ = this.store.select(getSelectedSales);
    salesDocument$ = this.openedDocument$.pipe(
        map(document => (document && Number(document.sellerCompanyId) === Number(this.configService.user.user.ActiveCompany)) ? document : null)
    );
    salesDocument: SalesDocument | null = null;

    groupingSchemas = groupingSchemas;

    buyDocumentsLength = 0;

    buyDocuments$ = this.openedDocument$.pipe(
        switchMap(document => {
            if (document) {
                if (Number(document.sellerCompanyId) === Number(this.configService.user.user.ActiveCompany)) {
                    return this.store.select(getBuyDocumentsForSelectedDocument);
                } else {
                    return of([document]);
                }
            } else {
                return of([] as SalesDocument[]);
            }
        })
    );
    buyDocuments: SalesDocument[] = [];

    openedDocuments: Record<string, SalesDocument> = {};

    allBuyPositionsMap$ = this.store.select(getBuyPositionsForSelectedDocument(true));
    buyPositionsMap$ = this.store.select(getBuyPositionsForSelectedDocument(false));
    connectedBuyPositions$ = this.buyPositionsMap$.pipe(
        map((positionsMap) =>
            Array.from(positionsMap.values()).reduce<Record<string, SalesDocumentPosition[]>>(
                (prev, cur) => {
                    if (!cur) {
                        return prev;
                    }
                    if (!prev[cur.documentId]) {
                        prev[cur.documentId] = [];
                    }
                    prev[cur.documentId].push(cur);
                    return prev;
                },
                {}
            )
        )
    );

    attachments$ = combineLatest([this.buyDocuments$, this.salesDocument$.pipe(map(doc => [doc]))]).pipe(
        switchMap((documents) => {
            return this.store.select(getDocumentsAttachments(documents.flat().filter(doc => doc).map(doc => doc.uuid)));
        }),
    );

    buyPositionsMap: Map<string, SalesDocumentPosition> = new Map<string, SalesDocumentPosition>();
    salesPositionsMap: Map<string, SalesDocumentPosition> = new Map<string, SalesDocumentPosition>();

    activeBuyDocument: SalesDocument | null = null;
    activeBuyDocument$ = new BehaviorSubject<SalesDocument | null>(null);

    allBuyPositions$ = combineLatest([
        this.buyDocuments$,
        this.activeBuyDocument$
    ]).pipe(
        switchMap(([buyDocuments, activeBuyDocument]) => {
            if (activeBuyDocument) {
                return combineLatest([
                    this.store.select(getSalesDocumentPositions(activeBuyDocument.uuid, true))
                ])
            } else {
                return combineLatest(buyDocuments.map(document => this.store.select(getSalesDocumentPositions(document.uuid, true))))
            }
        }),
        map(positions => [].concat(...positions) as SalesDocumentPosition[]),
        map((pos) =>
            pos.map((p) => ({
                ...p,
                configuration: p.configuration ? core.parseJson(p.configuration) : null,
                priceDiscounts: p.priceDiscounts ? core.parseJson(p.priceDiscounts) : [],
            }))
        )
    );

    buyPositions$ = this.allBuyPositions$.pipe(
        map((positions) => positions.filter(pos => !pos.isDeleted))
    );
    buyPositionsPerDocument$ = this.buyPositions$.pipe(map(positions => {
        const buyPositionsPerDocument: Record<string, SalesDocumentPosition[]> = {};
        positions.forEach(position => {
            const documentPositions = buyPositionsPerDocument[position.documentId] || [];
            documentPositions.push(position);
            buyPositionsPerDocument[position.documentId] = documentPositions;
        });
        return buyPositionsPerDocument;
    }));
    buyPositionsPerDocumentCount: Record<string, number> = {};

    groups$ =  this.mode$.pipe(
        switchMap(mode =>
            combineLatest([
                of(mode),
                mode === 'purchase'
                    ? combineLatest([this.buyDocuments$, this.activeBuyDocument$]).pipe(
                        map(([buyDocuments, activeBuyDocument]) =>
                            activeBuyDocument ? [activeBuyDocument] : buyDocuments
                        )
                    )
                    : this.salesDocument$.pipe(map(doc => [doc])),
                mode === 'purchase' ? this.buyPositions$ : this.positions$,
                defer(() => this.groupingControl.valueChanges.pipe(startWith(this.groupingControl.value))),
                defer(() => this.sortingControl.valueChanges.pipe(startWith(this.sortingControl.value))),
            ])
        ),
        filter(([, documents]) => documents.filter(doc => doc).length > 0),
        map(([mode, documents, positions, groupingId, sortingMode]) => {
            this.documentType = documents[0].salesDocumentTypeId;
            this.documentUuid = documents[0].uuid;
            this.documentNumber =  documents[0].documentNumber?.join('');
            this.setDocumentCrumbs();
            this.updateOpenedDocument(documents[0], mode);
            const groups: Record<string, {
                key: string;
                name: string;
                positions: DocumentPosition[];
                positionIds: string[];
                positionOrder?: Record<string, string | number>;
            }> = {};
            for (const document of documents) {
                const grouping = document.groups?.find((g) => g.groupSchemaId === Number(groupingId));
                if (grouping) {
                    grouping.groups.forEach((group) => {
                        const groupPositions = group.positionIds
                            .map((posId) => positions.find((p) => p.uuid === posId))
                            .filter((pos) => pos != null);
                        if (groups[group.key]) {
                            groups[group.key].positionIds.push(...group.positionIds);
                            groups[group.key].positions.push(...groupPositions);
                            if (group.positionOrder) {
                                groups[group.key].positionOrder = {
                                    ...groups[group.key].positionOrder,
                                    ...group.positionOrder,
                                };
                            }
                        } else {
                            groups[group.key] = {
                                ...group,
                                positions: [...groupPositions],
                                positionIds: [...group.positionIds],
                            };
                        }
                    });
                }
            }
            const sortFn = (a: DocumentPosition, b: DocumentPosition, positionOrder?: Record<string, string | number>) => {
                if (sortingMode === 'custom' && positionOrder) {
                    return positionOrder[a.uuid] < positionOrder[b.uuid] ? -1 : 1;
                } else if (sortingMode === 'name') {
                    return new Intl.Collator(this.configService.config.languageCode).compare(a.name, b.name);
                } else if (sortingMode === 'nameDesc') {
                    return new Intl.Collator(this.configService.config.languageCode).compare(b.name, a.name);
                } else if (sortingMode === 'createdDesc') {
                    if (+parseDate(a.created) === +parseDate(b.created)) {
                        return parseDate(a.rootCreated) > parseDate(b.rootCreated) ? -1 : 1;
                    }
                    return parseDate(a.created) > parseDate(b.created) ? -1 : 1;
                } else if (sortingMode !== 'custom') {
                    if (+parseDate(a.created) === +parseDate(b.created)) {
                        return parseDate(a.rootCreated) < parseDate(b.rootCreated) ? -1 : 1;
                    }
                    return parseDate(a.created) < parseDate(b.created) ? -1 : 1;
                }
                return 0;
            };
            if (Object.keys(groups).length > 0) {
                const result = Object.values(groups).map((group) => {
                    const sortedPositions = [...group.positions].sort((a, b) =>
                        sortFn(a, b, group.positionOrder)
                    );
                    sortedPositions.forEach((pos, index) => {
                        this.positionsIndexes.set(
                            pos.uuid,
                            group.name
                                ? `${this.translateService.instant(group.name)}/${index + 1}`
                                : `${index + 1}`
                        );
                    });
                    return { ...group, positions: sortedPositions };
                });
                return result;
            }
            const sortedPositions = [...positions].sort((a, b) => sortFn(a, b));
            sortedPositions.forEach((pos, index) => {
                this.positionsIndexes.set(pos.uuid, `${index + 1}`);
            });
            return [
                {
                    key: '',
                    name: '',
                    positions: sortedPositions,
                    positionIds: sortedPositions.map((p) => p.uuid),
                },
            ];
        }),
    );

    salesDeliveries$ = this.store.select(getDocDeliveries);
    buyDeliveries$ = combineLatest([
        this.buyDocuments$,
        this.activeBuyDocument$
    ]).pipe(
        switchMap(([buyDocuments, activeBuyDocument]) => {
            if (activeBuyDocument) {
                return this.store.select(getDocumentDeliveries(activeBuyDocument.uuid));
            } else {
                return combineLatest(buyDocuments.map(document => this.store.select(getDocumentDeliveries(document.uuid))));
            }
        }),
        map((deliveries) => [].concat(...deliveries) as Delivery[])
    );
    deliveries$ = this.mode$.pipe(
        switchMap(mode => mode === 'purchase' ? this.buyDeliveries$ : this.salesDeliveries$)
    );

    sorting = false;
    loadedConfiguratorCapabilities: string[] = [];
    loadingConfiguratorCapabilities = false;

    synced: Synced = Synced.Offline;
    syncedDocumentsState = new Map<string, Synced>();

    groupEditOptions: any[] = [];

    canViewBuyPrices = false;

    missingBuyDocument = false;

    requestComplaintAction: DocumentAction =
        {
            action: () => this.requestComplaint(),
            icon: 'comment',
            show: () => this.hasAccessToRequestComplaint(),
            disabled: () => this.synced !== 'synced',
            label: _('COMPLAINT|Zgłoś reklamację'),
            tooltip: undefined,
            appearance: IccAppearance.Secondary,
            side: 'right',
            testId: 'entire-order-complaint-button',
        };

    dataTranslatedAll = null;

    private subscriptions: Subscription[] = [];

    constructor(
        private store: Store<State>,
        private route: ActivatedRoute,
        private salesDocumentService: SalesDocumentService,
        private documentManagerService: DocumentManagerService,
        private priceVersionService: PriceVersionService,
        private exchangeRateService: ExchangeRateService,
        private configService: ConfigService,
        private router: Router,
        private translateService: TranslateService,
        private staticPositionDetailsService: StaticPositionDetailsService,
        private configuratorLoaderService: ConfiguratorLoaderService,
        private offerValidityService: OfferValidityService,
        private syncService: SyncService,
        private permissionsService: NgxPermissionsService,
        private salesDocumentPersonsService: SalesDocumentPersonsService,
        private personsService: PersonsService,
        private dialogService: DialogService,
        private statsService: StatsService,
        private changeDetectorRef: ChangeDetectorRef,
        private http: HttpClient,
        private productsService: ProductsService,
    ) {}

    async checkForInactiveProducts(positions: SalesDocumentPosition[]) {
        this.hasInactiveProducts = false;

        if (!positions?.length) {
            return;
        }

        const productIds = positions
            .filter(p => p.productId)
            .map(p => p.productId);

        if (productIds.length === 0) {
            return;
        }

        await this.productsService.loadData();
        const allProducts = (await this.productsService.getData())?.productsList;
        const productMap = new Map(allProducts.map(p => [p.id, p]));

        for (const position of positions) {
            if (position.productId) {
                const product = productMap.get(position.productId);
                if (!product || !product.isActive) {
                    this.hasInactiveProducts = true;
                    break;
                }
            }
        }
    }

    ngOnInit(): void {
        this.personsService.loaded.then(() => {
            this.departments = this.personsService.getDepartments();
        });
        this.switchToSalesOrder(this.route.snapshot.params.id).then(documentId => {
            this.store.dispatch(openDoc({ id: documentId }));
        });
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                this.switchToSalesOrder(params.id).then(documentId => {
                    this.store.dispatch(openDoc({ id: documentId }));
                });
            }),
            combineLatest([
                this.salesDocument$.pipe(
                    startWith(null)
                ),
                this.buyDocuments$
            ]).subscribe(([salesDocument, buyDocuments]) => {
                const documents = {};
                const openedDocuments = [];

                if (salesDocument) {
                    documents[salesDocument.uuid] = salesDocument;
                    openedDocuments.push(salesDocument.uuid);
                }

                buyDocuments.forEach(document => {
                    documents[document.uuid] = document;
                    openedDocuments.push(document.uuid);
                });
                this.salesDocument = salesDocument;
                this.buyDocuments = buyDocuments;
                this.buyDocumentsLength = buyDocuments.length;
                this.openedDocuments = documents;
                this.syncService.setCurrentDocumentsIds(Array.from(new Set(openedDocuments)));
                this.checkBuyAccess();
                this.updatedSynced();
            }),
            combineLatest([
                this.buyPositionsMap$,
                this.connectedBuyPositions$,
                this.activeBuyDocument$,
                this.positions$,
            ]).subscribe(([buyPositionsMap, connectedBuyPositions, activeBuyDocument, positions]) => {
                let filteredBuyPositionsMap: Map<string, SalesDocumentPosition> = new Map();
                if (activeBuyDocument) {
                    const activeBuyPositions = (connectedBuyPositions[activeBuyDocument.uuid] || []).reduce((acu, el) => {
                        acu.set(el.uuid, true);
                        return acu;
                    }, new Map<string, true>())
                    buyPositionsMap.forEach((value, key) => {
                        if (value && activeBuyPositions.has(value.uuid)) {
                            filteredBuyPositionsMap.set(key, value);
                        }
                    });
                } else {
                    filteredBuyPositionsMap = buyPositionsMap;
                }
                this.buyPositionsMap = filteredBuyPositionsMap;

                const salesPositionsMap: Map<string, SalesDocumentPosition> = new Map();
                positions.forEach(position => {
                    salesPositionsMap.set(position.uuid, position);
                })
                this.buyPositionsMap.forEach((value, key) => {
                    if (value) {
                        salesPositionsMap.set(value.uuid, salesPositionsMap.get(key));
                    }
                });
                this.salesPositionsMap = salesPositionsMap;
            }),
            this.mode$.pipe(
                switchMap(mode => mode === 'purchase' ? this.buyPositions$ : this.positions$)
            )
            .subscribe(async positions => {
                const positionsShowDetails = new Map<string, boolean>();
                const types = [];
                const configurations: Record<string, Record<string, any>> = {};
                for (const pos of positions) {
                    positionsShowDetails.set(pos.uuid, this.positionsShowDetails.get(pos.uuid) ?? false);
                    if (pos.type !== 'static' && pos.type !== 'group' && pos.type !== 'additional') {
                        if (!types.includes(pos.type)) {
                            types.push(pos.type);
                            configurations[pos.type] = {};
                        }
                        configurations[pos.type][pos.uuid] = pos.configuration;
                    }
                }
                this.positionsShowDetails = positionsShowDetails;

                if (types.length > 0) {
                    await this.loadConfiguratorCapabilities(types, configurations);
                } else {
                    this.groupEditOptions = [];
                }
                await this.updatePositionDetails(positions);
                await this.checkForInactiveProducts(positions);
            }),
            combineLatest([
                this.openedDocument$,
                this.openedDocumentId$,
                this.permissionsService.permissions$,
            ]).subscribe(([document, documentId]) => {
                if (!document && documentId) {
                    this.store.dispatch(openDoc({ id: null }));
                    this.router.navigate(['/trade']);
                }
                this.checkAccess(document);
            }),
            this.openedDocument$.pipe(
                distinctUntilChanged((a, b) => a?.uuid === b?.uuid)
            ).subscribe(document => {
                this.updateOpenCount(document);
            }),
            this.buyPositionsPerDocument$.subscribe(buyPositionsPerDocument => {
                this.buyPositionsPerDocumentCount = Object.keys(buyPositionsPerDocument).reduce((prev, cur) => {
                    prev[cur] = buyPositionsPerDocument[cur].length;
                    return prev;
                }, {});
            }),
            this.store.select(getSynced).subscribe(({synced}) => {
                this.syncedDocumentsState = synced;
                this.updatedSynced();
            }),
            this.positions$.pipe(
                distinctUntilChanged((a, b) => a.length === b.length && a.every((pos, index) => pos.uuid === b[index].uuid)),
                withLatestFrom(this.buyDocuments$.pipe(startWith([] as SalesDocument[])))
            ).subscribe(([positions, buyDocuments]) => {
                this.missingBuyDocument = positions.some(
                    (el) => el.type !== 'additional'
                ) && this.configService.user.user.ActiveCompanyLicenseType !== 'license' &&
                    buyDocuments.length === 0;
            }),
        );
    }

    ngAfterViewInit() {
        this.salesDocumentService.scrollToPosition();
    }

    ngOnDestroy() {
        this.subscriptions.map(el => el.unsubscribe());
    }

    hasAccessToRequestComplaint(){
        return [...this.buyPositionsMap.values()].every(pos => !pos.isInvalid) &&
            (this.activeBuyDocument || this.buyDocuments.length === 1) &&
            this.mode === 'purchase' &&
            this.canRequestComplaintV1;
    }

    requestComplaint() {
        this.router.navigate(['complaint-v1', 'complaint', 'new', this.getBuyProducerDocument().uuid]);
    }

    async switchToSalesOrder(buyOrderId: string) {
        this.syncService.setCurrentDocumentsIds([buyOrderId]);
        const salesDocument = await this.salesDocumentService.getSalesDocumentForBuyDocument(buyOrderId);
        if (salesDocument) {
            this.syncService.setCurrentDocumentsIds([buyOrderId, salesDocument.uuid]);
            return salesDocument.uuid;
        }
        return buyOrderId;
    }

    changeDescription(description: string) {
        this.documentManagerService.saveDocument(this.route.snapshot.params.id, {
            description,
        });
    }

    changeProducerNotes(producerNotes: string, document: SalesDocument) {
        if (document) {
            document.producerNotes = producerNotes;
            this.documentManagerService.saveDocument(document.uuid, {
                producerNotes,
            }, true);
        }
    }

    onActiveBuyDocumentChange(document: SalesDocument | null) {
        this.activeBuyDocument = document;
        this.activeBuyDocument$.next(document);
        this.checkBuyAccess();
    }

    onModeChange(mode: SalesDocumentViewMode) {
        this.mode = mode;
        this.mode$.next(mode);
        this.groupingControl.setValue(0);
        this.checkBuyAccess();
    }

    changeOrder(
        group: DocumentPositionGroup & { positions: SalesDocumentPosition[] },
        event: CdkDragDrop<string[]>
    ) {
        this.salesDocumentService.setPositionOrderInGroup(
            group,
            Number(this.groupingControl.value),
            group.positions,
            event.previousIndex,
            event.currentIndex
        );
    }

    // eslint-disable-next-line max-statements
    setDocumentCrumbs(){

        const crumbs = [];

        const state = history.state;
        let label = '';
        const commands = ['trade', 'offer', 'list'];
        if (state && state.documentType === this.documentType) {
            if (state.documentType === SalesDocumentType.Quotation) {
                commands.push('offers');
                label = this.translateService.instant('SALES_DOCUMENT|Lista ofert');
            } else if (state.documentType === SalesDocumentType.Inquiry) {
                commands.push('inquiries');
                if (state.side === 'sales') {
                    commands.push('sales');
                    label = this.translateService.instant('SALES_DOCUMENT|Lista zapytań ofertowych klientów końcowych');
                } else {
                    commands.push('purchase');
                    label = this.translateService.instant('SALES_DOCUMENT|Lista zapytań ofertowych u dostawcy');
                }
            } else if (state.documentType === SalesDocumentType.ServiceOrder) {
                commands.push('service-orders');
                label = this.translateService.instant('SALES_DOCUMENT|Lista zamówień serwisowych');
            } else {
                commands.push('orders');
                if (state.side === 'sales') {
                    commands.push('sales');
                    label = this.translateService.instant('SALES_DOCUMENT|Lista zamówień klientów końcowych');
                } else if (state.unplaced) {
                    commands.push('unplaced');
                    label = this.translateService.instant('SALES_DOCUMENT|Lista zamówień u dostawcy do potwierdzenia');
                } else {
                    commands.push('purchase');
                    label = this.translateService.instant('SALES_DOCUMENT|Lista zamówień u dostawcy');
                }
            }
        } else {
            if (this.documentType === SalesDocumentType.Quotation) {
                commands.push('offers');
                label = this.translateService.instant('SALES_DOCUMENT|Lista ofert');
            } else if (this.documentType === SalesDocumentType.Inquiry) {
                commands.push('inquiries', 'sales');
                label = this.translateService.instant('SALES_DOCUMENT|Lista zapytań ofertowych klientów końcowych');
            } else if (this.documentType === SalesDocumentType.ServiceOrder) {
                commands.push('service-orders');
                label = this.translateService.instant('SALES_DOCUMENT|Lista zamówień serwisowych');
            } else {
                commands.push('orders', 'sales');
                label = this.translateService.instant('SALES_DOCUMENT|Lista zamówień klientów końcowych');
            }
        }
        crumbs.push({
            label,
            url: '/' + commands.join('/'),
        });

        SalesDocumentComponent.crumbsSubject.next({
            level: 1,
            crumbs
        });

        if(this.documentUuid){
            const prefix = this.translateService.instant(SalesDocumentTypeDict[this.documentType]);

            label = this.documentNumber
                ? (this.activeBuyDocument || this.buyDocumentsLength === 1 || this.mode === 'sale') && this.mode !== 'comparison'
                    ? prefix + ' ' + (this.mode === 'sale' || this.buyDocumentsLength === 1 ? this.documentNumber : this.activeBuyDocument?.documentNumber.join(''))
                    : this.translateService.instant('INTERFACE|Różne wartości')
                : prefix;

            SalesDocumentComponent.crumbsSubject.next({
                level: 2,
                crumbs: [
                    {
                        label,
                        url: '/trade/offer/'+this.documentUuid,
                    }
                ],
            });
        }

    }

    async loadConfiguratorCapabilities(types: string[], configurations: Record<string, Record<string, any>>) {
        if (!this.loadingConfiguratorCapabilities) {
            this.loadingConfiguratorCapabilities = true;
            const moduleRef = await this.configuratorLoaderService.loadConfigurator();
            const configuratorsRepositoryService = this.configuratorLoaderService.configuratorsRepositoryService;
            this.groupEditOptions = [];
            for (const type of types) {
                let configuratorIds = [0]
                if (type === 'complex') {
                    configuratorIds = Object.values(configurations[type]).map(conf => conf.configuratorId);
                }
                for (const configuratorId of configuratorIds) {
                    if (!this.loadedConfiguratorCapabilities.includes(type + '-' + configuratorId)) {
                        await configuratorsRepositoryService.loadConfigurator(type, configuratorId, {
                            IccConfig: this.configService.config.config,
                            lang: this.configService.config.languageCode || 'en',
                        });
                        this.loadedConfiguratorCapabilities.push(type + '-' + configuratorId);
                    }
                    const groupEdit = this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getGroupEdit(type, configuratorId);
                    if (groupEdit) {
                        await moduleRef.instance.loadConfiguratorData(type, 0, 0, this.configService.config.languageCode ?? 'pl', false);
                        this.groupEditOptions.push(...groupEdit.documentOptions.map(option => ({
                            ...option,
                            callback: async () => {
                                configurations[type] = option.callback(configurations[type]);
                                await this.salesDocumentService.updatePositionsConfigurations(configurations[type]);
                            },
                            show: () => option.show(configurations[type])
                        })));
                    }

                }
            }

            this.loadingConfiguratorCapabilities = false;
        }
    }

    async updatePositionDetails(positions: SalesDocumentPosition[]) {
        const activeLang = this.configService.user.user.userBoxData.activeContentLanguage;

        const isOtherLang = (position: SalesDocumentPosition) => {
            return position.lang !== activeLang && position.lang || !position.lang && this.salesDocument?.lang !== activeLang && this.salesDocument?.lang;
        }

        const isTranslationNeeded = positions.some(isOtherLang);

        if (!this.dataTranslatedAll && isTranslationNeeded) {
            this.dataTranslatedAll = await this.http.get<any[]>(`/api/b2b/translated-data/${activeLang}`).toPromise();
        }
        for (const position of positions) {
            if (this.configuratorLoaderService.configuratorsRepositoryServiceStatic && this.dataTranslatedAll && isOtherLang(position) && position.type !== 'static' && position.type !== 'group' && position.type !== 'additional') {
                const rawConfig = typeof position.configuration === 'string'
                    ? core.parseJson(position.configuration)
                    : position.configuration;

                const modifiedDate = rawConfig?.modified
                    ? new Date(rawConfig.modified)
                    : new Date(position.created);

                const translatedMap = prepareTranslatedData(this.dataTranslatedAll, modifiedDate);

                const dataTranslated = Array.from(translatedMap.values());
                const configuratorClass = this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getConfigurations(position.type);

                const translatedConfig = translateConfiguration(configuratorClass.simple, rawConfig, dataTranslated, this.translateService.instant.bind(this.translateService));
                position.configuration = translatedConfig;

                // Translate categoryName in priceDiscounts
                this.translatePriceDiscountsCategoryNames(position, dataTranslated);
            } else if (this.dataTranslatedAll && isOtherLang(position) && (position.type === 'static' || position.type === 'additional')) {
                const translatedMap = prepareTranslatedData(this.dataTranslatedAll, position.created);
                const dataTranslated = Array.from(translatedMap.values());
                const record = dataTranslated.find((r) =>
                    r.model === 'Products' &&
                    (r.foreign_key == position?.configuration?.productId || r.foreign_key === position?.configuration?.productId) &&
                    r.field === 'name'
                );
                if (record && record.content) position.configuration.productName = record.content;
            }

            // Translate categoryName in priceDiscounts for all position types when translation is needed
            if (this.dataTranslatedAll && isOtherLang(position) && position.priceDiscounts?.length) {
                const modifiedDate = position.configuration?.modified
                    ? new Date(position.configuration.modified)
                    : new Date(position.created);
                const translatedMap = prepareTranslatedData(this.dataTranslatedAll, modifiedDate);
                const dataTranslated = Array.from(translatedMap.values());
                this.translatePriceDiscountsCategoryNames(position, dataTranslated);
            }

            if (position.title?.length) {
                this.positionsTitles.set(position.uuid, position.title);
            }
            if (this.staticPositionDetailsService.isStaticPosition(position)) {
                const tree = this.staticPositionDetailsService.getDetailsTree(position, this.translateService.instant.bind(this.translateService));
                this.positionsDetails.set(position.uuid, tree);
                if (!position.title?.length || isOtherLang(position)) {
                    const title = this.staticPositionDetailsService.getPositionTitle(position, this.translateService.instant.bind(this.translateService));
                    this.positionsTitles.set(position.uuid, title);
                }
            } else {
                if (this.configuratorLoaderService.configuratorsRepositoryServiceStatic) {
                    const configuratorId = position.type === 'complex' ? position.configuration.configuratorId : 0;
                    await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                        position.type,
                        configuratorId,
                        {
                            IccConfig: this.configService.config.config,
                            lang: this.configService.config.languageCode || 'en',
                        }
                    );
                    const getDetailsTree = this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getDetailsTree(position.type, configuratorId);
                    if (getDetailsTree) {
                        const tree = getDetailsTree(position.configuration, this.translateService.instant.bind(this.translateService));
                        this.positionsDetails.set(position.uuid, tree);
                    }
                    if (!position.title?.length || isOtherLang(position)) {
                        const getPositionTitle = this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getPositionTitle(position.type, configuratorId);
                        if (getPositionTitle) {
                            const title = getPositionTitle(position.configuration, this.translateService.instant.bind(this.translateService));
                            this.positionsTitles.set(position.uuid, title);
                        }
                    }
                }
            }
        }
    }

    get showGroupEditOptions() {
        return this.canPositionsBeEdited && this.groupEditOptions?.length && this.groupEditOptions?.some(el => el.show()) && (this.mode === 'comparison' || this.mode === 'sale') ;
    }

    async checkAccess(document: SalesDocument) {
        this.canPositionsBeEdited = false;
        if (document) {
            let canManage = false;
            if (document.salesDocumentTypeId === SalesDocumentType.Order) {
                canManage = await this.salesDocumentService.hasAccessToOrder(
                    document,
                    'manage'
                );
            } else if (document.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                canManage = await this.salesDocumentService.hasAccessToManageInquiry(
                    document,
                );
            } else if (document.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                canManage = await this.salesDocumentService.hasAccessToManageServiceOrder(
                    document,
                );
            } else {
                canManage = await this.salesDocumentService.hasAccessToManageQuotation(
                    document
                );
            }
            const { invalidOffer } = this.offerValidityService.getValidDate(document);
            this.canPositionsBeEdited =
                document.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                !document.isCanceled &&
                !invalidOffer &&
                document.externalSource !== 'ERP' &&
                !document.isConfirmed &&
                !document.isConfirmedOnlySalesPositions &&
                (document.salesDocumentTypeId === SalesDocumentType.Order || !document.isOrderPlaced) &&
                canManage;
            this.canViewBuyPrices = document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                await this.permissionsService.hasPermission('purchase-prices-view');

            this.canSalesProducerNotesRead = document.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                this.configService.user.user.ActiveCompanyLicenseType === 'license' &&
                await this.permissionsService.hasPermission('sell-producer-notes-manage');

            this.canSalesProducerNotesBeEdited = document.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !document.isCanceled &&
                !invalidOffer &&
                document.externalSource !== 'ERP' &&
                !document.isConfirmed &&
                !document.isConfirmedOnlySalesPositions &&
                (document.salesDocumentTypeId === SalesDocumentType.Order || !document.isOrderPlaced) &&
                document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                this.configService.user.user.ActiveCompanyLicenseType === 'license' &&
                await this.permissionsService.hasPermission('sell-producer-notes-manage') &&
                canManage;
            this.canRequestComplaintV1 = await this.permissionsService.hasPermission('trade-request-complaint-v1');
        }
    }

    async checkBuyAccess() {
        const document = this.getBuyProducerDocument();
        // When zbiorczo (no active buy doc selected) and multiple buy docs exist, use first doc for permission check
        const referenceDocument = document || (this.mode === 'purchase' && this.buyDocuments.length > 1 ? this.buyDocuments[0] : null);
        if (referenceDocument && this.mode === 'purchase') {
            let canManage = false;
            if (referenceDocument.salesDocumentTypeId === SalesDocumentType.Order) {
                canManage = await this.salesDocumentService.hasAccessToOrder(
                    referenceDocument,
                    'manage'
                );
            } else if (referenceDocument.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                canManage = await this.salesDocumentService.hasAccessToManageInquiry(
                    referenceDocument,
                );
            } else if (referenceDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                canManage = await this.salesDocumentService.hasAccessToManageServiceOrder(
                    referenceDocument,
                );
            } else {
                canManage = true;
            }
            const { invalidOffer } = document ? this.offerValidityService.getValidDate(document) : { invalidOffer: false };
            this.canBuyProducerNotesRead = referenceDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                referenceDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                await this.permissionsService.hasPermission('purchase-producer-notes-manage');
            // canBuyProducerNotesBeEdited only applies when a specific document is selected (not zbiorczo)
            this.canBuyProducerNotesBeEdited = !!document &&
                document.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                !document.isCanceled &&
                !invalidOffer &&
                document.externalSource !== 'ERP' &&
                !document.isConfirmed &&
                !document.isConfirmedOnlySalesPositions &&
                (document.salesDocumentTypeId === SalesDocumentType.Order || !document.isOrderPlaced) &&
                await this.permissionsService.hasPermission('purchase-producer-notes-manage') &&
                document.allowedEditBy !== 'sales' &&
                canManage;
            // Compute per-document editability for zbiorczo display
            const hasPermission = await this.permissionsService.hasPermission('purchase-producer-notes-manage');
            this.buyDocumentNotesEditable = {};
            for (const buyDoc of this.buyDocuments) {
                let buyDocCanManage = false;
                if (buyDoc.salesDocumentTypeId === SalesDocumentType.Order) {
                    buyDocCanManage = await this.salesDocumentService.hasAccessToOrder(buyDoc, 'manage');
                } else if (buyDoc.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                    buyDocCanManage = await this.salesDocumentService.hasAccessToManageServiceOrder(buyDoc);
                } else {
                    buyDocCanManage = true;
                }
                const { invalidOffer: docInvalidOffer } = this.offerValidityService.getValidDate(buyDoc);
                this.buyDocumentNotesEditable[buyDoc.uuid] =
                    buyDoc.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                    buyDoc.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                    !buyDoc.isCanceled &&
                    !docInvalidOffer &&
                    buyDoc.externalSource !== 'ERP' &&
                    !buyDoc.isConfirmed &&
                    !buyDoc.isConfirmedOnlySalesPositions &&
                    (buyDoc.salesDocumentTypeId === SalesDocumentType.Order || !buyDoc.isOrderPlaced) &&
                    hasPermission &&
                    buyDoc.allowedEditBy !== 'sales' &&
                    buyDocCanManage;
            }
        } else {
            this.canBuyProducerNotesBeEdited = false;
            this.canBuyProducerNotesRead = false;
            this.buyDocumentNotesEditable = {};
        }
    }

    switchShowDetails(posId: SalesDocumentPosition['uuid'], showDetails: boolean) {
        this.positionsShowDetails.set(posId, showDetails);
    }

    trackByPosition(index: number, el: SalesDocumentPosition) {
        return el.uuid;
    }
    trackByGroup(index: number, el: {
        key: string;
    }) {
        return el.key;
    }

    async updateOpenedDocument(document: SalesDocument, mode: SalesDocumentViewMode) {
        let canManage = false;
        if (document.salesDocumentTypeId === SalesDocumentType.Order) {
            canManage = await this.salesDocumentService.hasAccessToOrder(
                document,
                'manage'
            );
        } else if (document.salesDocumentTypeId === SalesDocumentType.Inquiry) {
            canManage = await this.salesDocumentService.hasAccessToManageInquiry(
                document,
            );
        } else {
            canManage = await this.salesDocumentService.hasAccessToManageQuotation(
                document
            );
        }
        if (!document.isOpened && mode === 'sale' && canManage) {
            document.isOpened = true;
            this.documentManagerService.saveDocument(document.uuid, {
                isOpened: true,
            }, true);
        }
    }

    getBuyProducerDocument() {
        return this.buyDocuments.length > 1 ? this.activeBuyDocument : this.buyDocuments[0];
    }

    getBuyProducerDocumentsForDisplay(): SalesDocument[] {
        if (this.activeBuyDocument) {
            return [this.activeBuyDocument];
        }
        return this.buyDocuments;
    }

    getDepartmentName(departmentId: number): string {
        const department = this.departments?.[departmentId];
        if (!department) {
            return '';
        }
        return typeof department === 'string' ? department : department.name;
    }

    initBuyProducerNote(doc: SalesDocument): void {
        this.buyProducerNotesEdit[doc.uuid] = doc.producerNotes || '';
    }

    initSalesProducerNote(doc: SalesDocument): void {
        this.salesProducerNotesEdit = doc.producerNotes || '';
    }

    getDeliveryDriver(delivery: Delivery) {
        return this.salesDocumentPersonsService.getDriver(
            delivery.driverUserId,
        );
    }

    getDeliveryStatus(delivery: Delivery): DeliveryStatus {
        if (delivery.isConfirmed) {
            if (Object.values(delivery.positions || {}).every((status) => status === 'delivered')) {
                return 'delivered';
            } else {
                return 'delivered-partially';
            }
        } else {
            let positionsCount = 0;
            if (+delivery.sellerCompanyId === +this.configService.user.user.ActiveCompany) {
                positionsCount = this.salesPositionsMap.size;
            } else {
                positionsCount = this.buyPositionsPerDocumentCount[delivery.documentId] || 0;
            }
            if (Object.keys(delivery.positions || {})?.length === positionsCount) {
                return 'sent';
            } else {
                return 'sent-partially';
            }
        }
    }

    getDeliveryStatusLabel(delivery: Delivery) {
        return this.translateService.instant(DeliveryStatuses[this.getDeliveryStatus(delivery)]);
    }

    getDeliveryStatusColor(delivery: Delivery) {
        return DeliveryStatusesColors[this.getDeliveryStatus(delivery)];
    }

    getPositionDeliveryStatusLabel(status: 'delivered' | 'not-delivered') {
        return this.translateService.instant(DeliveryStatuses[status]);
    }

    getPositionDeliveryStatusColor(status: 'delivered' | 'not-delivered') {
        return DeliveryStatusesColors[status];
    }

    openDeliverySignaturePreview(delivery: Delivery) {
        this.dialogService.open(DeliverySignaturePreviewComponent, {
            data: {
                signatureImage: delivery.signatureImage,
            }
        });
    }

    updateOpenCount(document: SalesDocument) {
        if (document) {
            let statsKey = 'quotation';
            if (document.salesDocumentTypeId === SalesDocumentType.Order) {
                statsKey = 'order';
            } else if (document.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                statsKey = 'inquiry';
            } else if (document.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                statsKey = 'service-order';
            }
            this.statsService.add({
                action: `open-${statsKey}-document`,
            });
        }
    }

    updatedSynced() {
        const openedDocumentsIds = [];

        if (this.salesDocument) {
            openedDocumentsIds.push(this.salesDocument.uuid);
        }
        if (this.buyDocuments?.length) {
            this.buyDocuments?.forEach((document) => {
                openedDocumentsIds.push(document.uuid);
            });
        }
        setTimeout(() => {
            this.synced = this.syncService.getSynced(
                openedDocumentsIds,
                this.syncedDocumentsState
            );
            this.changeDetectorRef.detectChanges();
        }, 0);
    }

    /**
     * Translate categoryName in priceDiscounts using WindowAccessoriesCategories translations
     */
    private translatePriceDiscountsCategoryNames(position: SalesDocumentPosition, dataTranslated: any[]) {
        if (!position.priceDiscounts?.length) {
            return;
        }

        const priceDiscounts = typeof position.priceDiscounts === 'string'
            ? core.parseJson(position.priceDiscounts)
            : position.priceDiscounts;

        for (const discount of priceDiscounts) {
            if ('categoryId' in discount && discount.categoryId && discount.categoryName) {
                const record = dataTranslated.find((r) =>
                    r.model === 'WindowAccessoriesCategories' &&
                    r.foreign_key == discount.categoryId &&
                    r.field === 'name'
                );
                if (record && record.content) {
                    discount.categoryName = record.content;
                }
            }
        }

        position.priceDiscounts = priceDiscounts;
    }
}
